import { toFieldKey } from '../../utils/helpers'

export const FIELD_TYPES = [
  { type: 'TEXT', label: 'Text', hint: 'Single line' },
  { type: 'TEXTAREA', label: 'Long text', hint: 'Multiple lines' },
  { type: 'EMAIL', label: 'Email' },
  { type: 'PHONE', label: 'Phone' },
  { type: 'URL', label: 'Website' },
  { type: 'NUMBER', label: 'Number', hint: 'Whole number' },
  { type: 'DECIMAL', label: 'Decimal' },
  { type: 'CURRENCY', label: 'Currency' },
  { type: 'DATE', label: 'Date' },
  { type: 'DATETIME', label: 'Date & time' },
  { type: 'CHECKBOX', label: 'Checkbox', hint: 'Yes / no' },
  { type: 'SELECT', label: 'Dropdown', hint: 'Pick one' },
  { type: 'RADIO', label: 'Radio', hint: 'Pick one, all visible' },
  { type: 'MULTI_SELECT', label: 'Multi-select', hint: 'Pick many' },
  { type: 'LOOKUP', label: 'Lookup', hint: 'Link to another record' },
  { type: 'USER', label: 'Person', hint: 'A workspace member, e.g. Owner' },
  { type: 'ROLLUP', label: 'Roll-up', hint: 'Count or total of linked records' },
]

export const emptyRollup = () => ({ entityType: '', via: '', fn: 'COUNT', field: '', filterField: '', filterValue: '', descendants: false })
export const defaultParentLink = () => ({ onParentDelete: 'CASCADE', display: 'TABLE' })

export const fieldTypeLabel = (type) => FIELD_TYPES.find((t) => t.type === type)?.label || type

// Mirrors FieldType on the backend; GET /forms/field-types is the source of truth when loaded
export const capabilities = (type, fieldTypes) => {
  const remote = fieldTypes?.find((t) => t.type === type)
  if (remote) return remote
  const text = ['TEXT', 'TEXTAREA', 'EMAIL', 'PHONE', 'URL'].includes(type)
  return {
    supportsOptions: ['SELECT', 'MULTI_SELECT', 'RADIO'].includes(type),
    supportsLength: text,
    supportsRange: ['NUMBER', 'DECIMAL', 'CURRENCY'].includes(type),
    supportsPattern: text,
    supportsLookup: type === 'LOOKUP',
    supportsRollup: type === 'ROLLUP',
  }
}

let counter = 0
export const uid = () => `u${Date.now().toString(36)}${(counter++).toString(36)}`

const uniqueKey = (base, existingKeys) => {
  let key = base || 'field'
  let n = 2
  while (existingKeys.has(key)) key = `${base}${n++}`
  return key
}

export const newField = (type, existingKeys) => {
  const label = fieldTypeLabel(type)
  const field = {
    uid: uid(),
    fieldKey: uniqueKey(toFieldKey(label), existingKeys),
    keyTouched: false,
    label,
    fieldType: type,
    required: false,
    readOnly: false,
    hidden: false,
    placeholder: '',
    helpText: '',
    defaultValue: '',
    minLength: '',
    maxLength: '',
    minValue: '',
    maxValue: '',
    pattern: '',
    patternMessage: '',
    lookupEntityType: '',
    parentLink: null,
    requiredWhen: null,
    rollup: type === 'ROLLUP' ? emptyRollup() : null,
    options: [],
  }
  if (['SELECT', 'MULTI_SELECT', 'RADIO'].includes(type)) {
    field.options = [newOption('Option 1'), newOption('Option 2')]
  }
  return field
}

export const newOption = (label) => ({
  uid: uid(),
  label,
  value: toOptionValue(label),
  valueTouched: false,
})

export const toOptionValue = (label) =>
  label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 200)

export const newSection = (title = 'New section') => ({ uid: uid(), title, description: '', columns: 2, fields: [] })

const str = (v) => (v === null || v === undefined ? '' : String(v))

/** FormResponse → editor state. Keys of saved fields count as "touched": they must stay stable. */
export const fromResponse = (form) => ({
  meta: {
    id: form.id,
    status: form.status,
    version: form.version,
    code: form.code,
    entityType: form.entityType,
    name: form.name,
    description: str(form.description),
  },
  sections: form.sections.map((s) => ({
    uid: uid(),
    title: s.title,
    description: str(s.description),
    columns: s.columns || 1,
    fields: s.fields.map((f) => ({
      uid: uid(),
      fieldKey: f.fieldKey,
      keyTouched: true,
      label: f.label,
      fieldType: f.fieldType,
      required: !!f.required,
      readOnly: !!f.readOnly,
      hidden: !!f.hidden,
      placeholder: str(f.placeholder),
      helpText: str(f.helpText),
      defaultValue: str(f.defaultValue),
      minLength: str(f.minLength),
      maxLength: str(f.maxLength),
      minValue: str(f.minValue),
      maxValue: str(f.maxValue),
      pattern: str(f.pattern),
      patternMessage: str(f.patternMessage),
      lookupEntityType: str(f.lookupEntityType),
      parentLink: f.parentLink || null,
      requiredWhen: f.requiredWhen || null,
      rollup: f.rollup ? { ...emptyRollup(), ...f.rollup, field: str(f.rollup.field), filterField: str(f.rollup.filterField), filterValue: str(f.rollup.filterValue) } : null,
      options: f.options.map((o) => ({ uid: uid(), value: o.value, label: o.label, valueTouched: true })),
    })),
  })),
})

export const emptyState = () => ({
  meta: { id: null, status: 'DRAFT', version: null, code: '', entityType: '', name: '', description: '' },
  sections: [{ ...newSection('Details'), fields: [] }],
})

const blank = (v) => (v === '' || v === undefined ? null : v)
const num = (v) => (v === '' || v === undefined || v === null ? null : Number(v))

/** Editor state → FormRequest */
export const toRequest = ({ meta, sections }) => ({
  entityType: meta.entityType.trim(),
  code: meta.code.trim(),
  name: meta.name.trim(),
  description: blank(meta.description.trim()),
  sections: sections.map((s) => ({
    title: s.title.trim(),
    description: blank(s.description.trim()),
    columns: s.columns,
    fields: s.fields.map((f) => {
      const caps = capabilities(f.fieldType)
      const rollup = f.fieldType === 'ROLLUP' && f.rollup
      return {
        fieldKey: f.fieldKey.trim(),
        label: f.label.trim(),
        fieldType: f.fieldType,
        required: f.required,
        hidden: f.hidden,
        placeholder: blank(f.placeholder.trim()),
        helpText: blank(f.helpText.trim()),
        minLength: caps.supportsLength ? num(f.minLength) : null,
        maxLength: caps.supportsLength ? num(f.maxLength) : null,
        minValue: caps.supportsRange ? num(f.minValue) : null,
        maxValue: caps.supportsRange ? num(f.maxValue) : null,
        pattern: caps.supportsPattern ? blank(f.pattern.trim()) : null,
        patternMessage: caps.supportsPattern ? blank(f.patternMessage.trim()) : null,
        lookupEntityType: caps.supportsLookup ? blank(f.lookupEntityType.trim()) : null,
        defaultValue: caps.supportsLookup || rollup ? null : blank(f.defaultValue.trim()),
        readOnly: caps.supportsLookup || rollup ? false : f.readOnly,
        ...(rollup ? { required: false } : {}),
        parentLink: caps.supportsLookup ? f.parentLink || null : null,
        // stage rule: required only when another field has one of some values
        requiredWhen: !rollup && !f.required && f.requiredWhen?.field && f.requiredWhen.values?.length
          ? { field: f.requiredWhen.field, values: f.requiredWhen.values } : null,
        rollup: rollup ? {
          entityType: rollup.entityType,
          via: rollup.via,
          fn: rollup.fn,
          field: rollup.fn === 'COUNT' ? null : blank(rollup.field),
          filterField: blank(rollup.filterField),
          filterValue: rollup.filterField ? blank(String(rollup.filterValue ?? '')) : null,
          descendants: !!rollup.descendants,
        } : null,
        options: caps.supportsOptions ? f.options.map((o) => ({ value: o.value.trim(), label: o.label.trim() })) : [],
      }
    }),
  })),
})

/**
 * Checks worth catching before a round trip. Returns { meta: {name: msg}, items: {uid: [msg]} }.
 * The backend re-validates everything.
 */
export const validateLocally = ({ meta, sections }) => {
  const errors = { meta: {}, items: {} }
  const add = (id, msg) => { (errors.items[id] = errors.items[id] || []).push(msg) }

  if (!meta.name.trim()) errors.meta.name = 'Name is required'
  if (!/^[A-Z][A-Z0-9_]{1,49}$/.test(meta.entityType.trim())) errors.meta.entityType = 'UPPER_SNAKE_CASE, e.g. LEAD'
  if (!/^[a-z][a-z0-9_-]{1,99}$/.test(meta.code.trim())) errors.meta.code = 'Lowercase letters, digits, - or _; starts with a letter'

  const keys = new Map()
  const parentLinks = sections.flatMap((s) => s.fields).filter((f) => f.fieldType === 'LOOKUP' && f.parentLink)
  if (parentLinks.length > 1) parentLinks.slice(1).forEach((f) => add(f.uid, 'Only one field can be the parent link'))
  sections.forEach((s) => {
    if (!s.title.trim()) add(s.uid, 'Section title is required')
    s.fields.forEach((f) => {
      if (!f.label.trim()) add(f.uid, 'Label is required')
      if (!/^[a-z][a-zA-Z0-9_]{0,99}$/.test(f.fieldKey)) add(f.uid, 'Key must start with a lowercase letter; letters, digits, _')
      if (keys.has(f.fieldKey)) add(f.uid, `Key "${f.fieldKey}" is used twice`)
      keys.set(f.fieldKey, f.uid)
      if (f.fieldType === 'LOOKUP' && !/^[A-Z][A-Z0-9_]{1,49}$/.test(f.lookupEntityType || '')) {
        add(f.uid, 'Choose which record type this lookup links to')
      }
      if (f.fieldType === 'ROLLUP') {
        const r = f.rollup || {}
        if (!r.entityType || !r.via) add(f.uid, 'Choose the linked records and the link field to roll up')
        if (r.fn !== 'COUNT' && !r.field) add(f.uid, 'Choose the field to total')
        if (!!r.filterField !== !!String(r.filterValue ?? '')) add(f.uid, 'A filter needs both a field and a value')
      }
      if (capabilities(f.fieldType).supportsOptions) {
        if (f.options.length === 0) add(f.uid, 'Add at least one option')
        const values = new Set()
        f.options.forEach((o) => {
          if (!o.value.trim() || !o.label.trim()) add(f.uid, 'Options need a label and a value')
          if (values.has(o.value)) add(f.uid, `Option value "${o.value}" is used twice`)
          values.add(o.value)
        })
      }
    })
  })
  return errors
}

export const hasErrors = (errors) => Object.keys(errors.meta).length > 0 || Object.keys(errors.items).length > 0

/**
 * Backend errors ("sections[0].fields[1] (key): msg", "sections[0].fields[1].fieldKey: msg",
 * "name: msg") → same shape as validateLocally, using the indices of the state that was sent.
 */
export const mapServerErrors = (messages, { sections }) => {
  const errors = { meta: {}, items: {}, general: [] }
  messages.forEach((message) => {
    const m = message.match(/^sections\[(\d+)\](?:\.fields\[(\d+)\])?(?:\.[\w[\].]+)?(?: \([^)]*\))?: (.*)$/)
    if (m) {
      const section = sections[Number(m[1])]
      const target = m[2] !== undefined ? section?.fields[Number(m[2])] : section
      if (target) {
        errors.items[target.uid] = [...(errors.items[target.uid] || []), m[3]]
        return
      }
    }
    const meta = message.match(/^(name|code|entityType|description): (.*)$/)
    if (meta) errors.meta[meta[1]] = meta[2]
    else errors.general.push(message)
  })
  return errors
}

export const allFieldKeys = (sections, exceptUid) =>
  new Set(sections.flatMap((s) => s.fields).filter((f) => f.uid !== exceptUid).map((f) => f.fieldKey))
